import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp,
  Users,
  CreditCard,
  UserPlus,
  ShoppingBag,
  Wallet,
  Building2,
  User,
  Banknote
} from 'lucide-react';

// Funciones auxiliares para detectar consumidor final
const isConsumerFinalByNit = (taxId: string | null): boolean => {
  if (!taxId) return true;
  const cleanTaxId = taxId.replace(/\D/g, ''); // Solo números
  return cleanTaxId === 'CF' || cleanTaxId === '' || cleanTaxId === '0' || cleanTaxId.length === 0;
};

const isConsumerFinalByName = (name: string | null): boolean => {
  if (!name) return true;
  const lowerName = name.toLowerCase();
  return lowerName.includes('consumidor') || 
         lowerName.includes('generico') || 
         lowerName.includes('cf') ||
         lowerName.includes('final') ||
         lowerName === 'sin nombre' ||
         lowerName === '';
};
import { useNavigate } from 'react-router-dom';

interface SalesData {
  salesByUser: {
    user_id: string;
    user_name: string;
    sales_count: number;
    total_amount: number;
    branch_name?: string;
  }[];
  salesByBranch: {
    branch_id: string;
    branch_name: string;
    sales_count: number;
    total_amount: number;
  }[];
  paymentMethods: {
    method: string;
    count: number;
    percentage: number;
    total_amount: number;
  }[];
  customerStats: {
    new_customers: number;
    generic_customers: number;
    total_sales_today: number;
    new_customer_percentage: number;
  };
}

interface SalesIndicatorsProps {
  branchId?: string;
}

export function SalesIndicators({ branchId }: SalesIndicatorsProps) {
  const { selectedCompany, user } = useAuth();
  const navigate = useNavigate();
  const [salesData, setSalesData] = useState<SalesData>({
    salesByUser: [],
    salesByBranch: [],
    paymentMethods: [],
    customerStats: {
      new_customers: 0,
      generic_customers: 0,
      total_sales_today: 0,
      new_customer_percentage: 0
    }
  });
  const [loading, setLoading] = useState(true);

  const loadSalesData = async () => {
    if (!selectedCompany?.id) return;

    try {
      setLoading(true);
      
      // 1. Obtener sesión activa del usuario para usar su operational_date
      const { data: activeSession } = await supabase
        .from('cash_register_sessions')
        .select('operational_date, session_date')
        .eq('company_id', selectedCompany.id)
        .eq('user_id', user.id)
        .is('closed_at', null)
        .order('opened_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // 2. Usar operational_date si existe, sino usar fecha actual
      let targetDate: string;
      if (activeSession?.operational_date) {
        targetDate = activeSession.operational_date;
        console.log('📅 Indicadores - Usando fecha operativa simulada:', targetDate);
      } else {
        const now = new Date();
        const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
        targetDate = localDate.toISOString().split('T')[0];
        console.log('📅 Indicadores - Usando fecha actual:', targetDate);
      }
      
      // 3. Crear timestamps para inicio y fin del día
      const todayStart = new Date(targetDate + 'T00:00:00');
      const todayEnd = new Date(targetDate + 'T23:59:59.999');

      // Obtener ventas básicas del día - usando solo campos que existen con seguridad
      const { data: salesDataRaw, error: salesError } = await supabase
        .from('sales')
        .select('*')
        .eq('company_id', selectedCompany.id)
        .in('status', ['completed', 'certified'])
        .eq('sale_date', targetDate)
        .order('sale_date', { ascending: false });

      if (salesError) {
        console.error('Error fetching sales:', salesError);
        throw salesError;
      }

      // Solo loguear si hay debugging activo
      if (process.env.NODE_ENV === 'development') {
        console.log('Raw sales data:', salesDataRaw);
      }

      // Si no hay ventas, mostrar datos vacíos
      if (!salesDataRaw || salesDataRaw.length === 0) {
        setSalesData({
          salesByUser: [],
          salesByBranch: [],
          paymentMethods: [],
          customerStats: {
            new_customers: 0,
            generic_customers: 0,
            total_sales_today: 0,
            new_customer_percentage: 0
          }
        });
        return;
      }

      // 🔒 SECURITY: Obtener información de usuarios filtrados por empresa
      const userIds = [...new Set(salesDataRaw.map((s: any) => s.cashier_id).filter(Boolean))];
      let userProfiles: any[] = [];
      if (userIds.length > 0) {
        const { getProfilesByCompany } = await import('@/utils/profileQueries');
        const { data: profiles } = await getProfilesByCompany(userIds, selectedCompany.id);
        userProfiles = profiles || [];
      }

      // Obtener información de sucursales
      const { data: branchData } = await supabase
        .from('branches')
        .select('id, name')
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true);
      const branches = branchData || [];

      // Obtener información de clientes para análisis correcto
      const customerIds = [...new Set(salesDataRaw.map((s: any) => s.customer_id).filter(Boolean))];
      let customersData: any[] = [];
      if (customerIds.length > 0) {
        const { data: customers } = await supabase
          .from('customers')
          .select('id, name, tax_id, es_consumidor_final, created_at')
          .in('id', customerIds);
        customersData = customers || [];
      }

      // Procesar datos directamente de las ventas reales
      const salesByUserData = salesDataRaw.map((sale: any) => {
        const userId = sale.cashier_id; // Usar el campo correcto cashier_id
        const userProfile = userProfiles.find(p => p.user_id === userId);
        const branch = branches.find(b => b.id === sale.branch_id) || branches[0];
        const customer = customersData.find(c => c.id === sale.customer_id);
        
        // Crear un nombre descriptivo si no se encuentra el perfil
        const userName = userProfile?.full_name || userProfile?.email || `Vendedor ${userId?.substring(0, 8) || 'N/A'}`;
        
        return {
          id: sale.id,
          user_id: userId,
          created_at: sale.created_at,
          total_amount: sale.total_amount || 0,
          payment_method: translatePaymentMethod(sale.payment_method || 'No especificado'),
          customer_id: sale.customer_id,
          branch_id: sale.branch_id || branch?.id,
          profiles: { full_name: userName },
          branches: branch || { name: 'Sucursal principal' },
          customers: customer
        };
      });

      // Procesar datos por vendedor
      const userSales = salesByUserData?.reduce((acc: any[], sale: any) => {
        const existingUser = acc.find(u => u.user_id === sale.user_id);
        if (existingUser) {
          existingUser.sales_count += 1;
          existingUser.total_amount += sale.total_amount;
        } else {
          acc.push({
            user_id: sale.user_id,
            user_name: sale.profiles?.full_name || `Vendedor ${sale.user_id?.substring(0, 8) || 'N/A'}`,
            sales_count: 1,
            total_amount: sale.total_amount,
            branch_name: sale.branches?.name
          });
        }
        return acc;
      }, []) || [];

      // Procesar datos por sucursal
      const branchSales = salesByUserData?.reduce((acc: any[], sale: any) => {
        const existingBranch = acc.find(b => b.branch_id === sale.branch_id);
        if (existingBranch) {
          existingBranch.sales_count += 1;
          existingBranch.total_amount += sale.total_amount;
        } else {
          acc.push({
            branch_id: sale.branch_id,
            branch_name: sale.branches?.name || 'Sucursal desconocida',
            sales_count: 1,
            total_amount: sale.total_amount
          });
        }
        return acc;
      }, []) || [];

      // Procesar métodos de pago
      interface PaymentMethodStat {
        method: string;
        count: number;
        total_amount: number;
      }
      
      const paymentMethodStats: Record<string, PaymentMethodStat> = salesByUserData?.reduce((acc: Record<string, PaymentMethodStat>, sale: any) => {
        const method = sale.payment_method; // Ya está traducido arriba
        if (acc[method]) {
          acc[method].count += 1;
          acc[method].total_amount += sale.total_amount;
        } else {
          acc[method] = {
            method,
            count: 1,
            total_amount: sale.total_amount
          };
        }
        return acc;
      }, {}) || {};

      const totalSales = Object.values(paymentMethodStats).reduce((sum: number, pm: PaymentMethodStat) => sum + pm.count, 0);
      const paymentMethods = Object.values(paymentMethodStats).map((pm: PaymentMethodStat) => ({
        ...pm,
        percentage: totalSales > 0 ? (pm.count / totalSales) * 100 : 0
      })).sort((a, b) => b.count - a.count);

      // Estadísticas de clientes (nuevos vs consumidor final)
      const newCustomers = salesByUserData?.filter((sale: any) => {
        if (!sale.customer_id || !sale.customers) return false;
        
        // Cliente creado el mismo día objetivo (fecha operativa)
        const customerCreated = new Date(sale.customers.created_at);
        const target = new Date(targetDate + 'T12:00:00');
        const isCreatedToday = customerCreated.toDateString() === target.toDateString();
        
        // No es consumidor final
        const isNotConsumerFinal = !sale.customers.es_consumidor_final && 
                                   !isConsumerFinalByNit(sale.customers.tax_id) &&
                                   !isConsumerFinalByName(sale.customers.name);
        
        return isCreatedToday && isNotConsumerFinal;
      }).length || 0;

      const genericCustomers = salesByUserData?.filter((sale: any) => {
        // Sin customer_id o marcado como consumidor final
        if (!sale.customer_id) return true;
        
        if (sale.customers) {
          return sale.customers.es_consumidor_final || 
                 isConsumerFinalByNit(sale.customers.tax_id) ||
                 isConsumerFinalByName(sale.customers.name);
        }
        
        return false;
      }).length || 0;

      const customerStats = {
        new_customers: newCustomers,
        generic_customers: genericCustomers,
        total_sales_today: salesByUserData?.length || 0,
        new_customer_percentage: salesByUserData?.length > 0 ? (newCustomers / salesByUserData.length) * 100 : 0
      };

      setSalesData({
        salesByUser: userSales.slice(0, 5), // Top 5 vendedores
        salesByBranch: branchSales.slice(0, 5), // Top 5 sucursales
        paymentMethods: paymentMethods.slice(0, 4), // Top 4 métodos de pago
        customerStats
      });

    } catch (error) {
      console.error('Error loading sales data:', error);
      // Usar datos de demostración en caso de error
      setSalesData({
        salesByUser: [
          { user_id: '1', user_name: 'María García', sales_count: 12, total_amount: 3450.50, branch_name: 'Centro' },
          { user_id: '2', user_name: 'Carlos López', sales_count: 8, total_amount: 2890.25, branch_name: 'Norte' }
        ],
        salesByBranch: [
          { branch_id: '1', branch_name: 'Centro', sales_count: 25, total_amount: 8750.75 },
          { branch_id: '2', branch_name: 'Norte', sales_count: 18, total_amount: 5230.40 }
        ],
        paymentMethods: [
          { method: 'Efectivo', count: 15, percentage: 60, total_amount: 4200.50 },
          { method: 'Tarjeta', count: 8, percentage: 32, total_amount: 3100.25 },
          { method: 'Transferencia', count: 2, percentage: 8, total_amount: 1150.00 }
        ],
        customerStats: {
          new_customers: 5,
          generic_customers: 12,
          total_sales_today: 25,
          new_customer_percentage: 20
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSalesData();
    // Actualizar cada 2 minutos para tiempo real
    const interval = setInterval(loadSalesData, 2 * 60 * 1000);
    
    // Configurar suscripción en tiempo real para actualizaciones automáticas
    const channel = supabase
      .channel('sales-realtime')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'sales' }, 
        () => {
          console.log('Sale updated, refreshing data...');
          loadSalesData();
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [selectedCompany?.id, branchId]);

  const { formatCurrency } = useTenantConfig();

  // Función para traducir método de pago al español
  const translatePaymentMethod = (method: string): string => {
    const translations: Record<string, string> = {
      'cash': 'Efectivo',
      'efectivo': 'Efectivo',
      'card': 'Tarjeta',
      'tarjeta': 'Tarjeta',  
      'credit_card': 'Tarjeta',
      'transfer': 'Transferencia',
      'transferencia': 'Transferencia',
      'bank_transfer': 'Transferencia',
      'credit': 'Crédito',
      'credito': 'Crédito',
      'check': 'Cheque',
      'cheque': 'Cheque'
    };
    
    const lowerMethod = method.toLowerCase().trim();
    return translations[lowerMethod] || method || 'No especificado';
  };

  const getPaymentMethodIcon = (method: string) => {
    const translatedMethod = translatePaymentMethod(method).toLowerCase();
    switch (translatedMethod.toLowerCase()) {
      case 'efectivo':
        return Banknote;
      case 'tarjeta':
        return CreditCard;
      case 'transferencia':
        return Wallet;
      case 'crédito':
        return Wallet;
      default:
        return Wallet;
    }
  };

  const getPaymentMethodColor = (method: string) => {
    const translatedMethod = translatePaymentMethod(method).toLowerCase();
    switch (translatedMethod.toLowerCase()) {
      case 'efectivo':
        return 'bg-status-success-bg text-status-success';
      case 'tarjeta':
        return 'bg-status-info-bg text-status-info';
      case 'transferencia':
        return 'bg-primary/10 text-primary';
      case 'crédito':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
            <CardHeader className="pb-4">
              <div className="animate-pulse">
                <div className="h-6 bg-muted/50 rounded w-32 mb-2"></div>
                <div className="h-4 bg-muted/50 rounded w-24"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse space-y-4">
                <div className="h-20 bg-muted/50 rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Ventas por Vendedor */}
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center text-lg font-semibold">
            <div className="h-8 w-8 rounded-lg bg-status-info-bg flex items-center justify-center mr-3">
              <User className="h-4 w-4 text-status-info" />
            </div>
            Ventas por Vendedor
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground font-medium">
            Rendimiento individual hoy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {salesData.salesByUser.length > 0 ? (
            salesData.salesByUser.map((user, index) => (
              <div key={user.user_id} className="flex items-center justify-between p-4 bg-background/60 backdrop-blur-sm rounded-xl border border-border/20">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{user.user_name}</p>
                    <p className="text-xs text-muted-foreground">{user.branch_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-foreground">{user.sales_count} ventas</p>
                  <p className="text-sm text-status-success font-semibold">{formatCurrency(user.total_amount)}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay ventas registradas hoy</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ventas por Sucursal */}
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center text-lg font-semibold">
            <div className="h-8 w-8 rounded-lg bg-status-success-bg flex items-center justify-center mr-3">
              <Building2 className="h-4 w-4 text-status-success" />
            </div>
            Ventas por Sucursal
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground font-medium">
            Rendimiento por ubicación
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {salesData.salesByBranch.length > 0 ? (
            salesData.salesByBranch.map((branch, index) => (
              <div key={branch.branch_id} className="flex items-center justify-between p-4 bg-background/60 backdrop-blur-sm rounded-xl border border-border/20">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-status-success-bg text-status-success font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{branch.branch_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-foreground">{branch.sales_count} ventas</p>
                  <p className="text-sm text-status-success font-semibold">{formatCurrency(branch.total_amount)}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay ventas por sucursal hoy</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Métodos de Pago Predominantes */}
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center text-lg font-semibold">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
              <CreditCard className="h-4 w-4 text-primary" />
            </div>
            Formas de Pago
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground font-medium">
            Métodos más utilizados hoy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {salesData.paymentMethods.length > 0 ? (
            salesData.paymentMethods.map((method, index) => {
              const Icon = getPaymentMethodIcon(method.method);
              return (
                <div key={method.method} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5 text-muted-foreground" />
                      <span className="font-semibold text-foreground">{method.method}</span>
                    </div>
                    <div className="text-right">
                      <Badge className={getPaymentMethodColor(method.method)}>
                        {method.count} ({method.percentage.toFixed(1)}%)
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <Progress value={method.percentage} className="flex-1 mr-4 h-2" />
                    <span className="font-semibold text-status-success min-w-fit">
                      {formatCurrency(method.total_amount)}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay métodos de pago registrados</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estadísticas de Clientes */}
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center text-lg font-semibold">
            <div className="h-8 w-8 rounded-lg bg-warning/10 flex items-center justify-center mr-3">
              <UserPlus className="h-4 w-4 text-warning" />
            </div>
            Clientes del Día
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground font-medium">
            Nuevos vs Genéricos hoy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-status-success-bg rounded-xl">
              <div className="text-2xl font-bold text-status-success">
                {salesData.customerStats.new_customers}
              </div>
              <div className="text-sm text-status-success font-medium">
                Clientes Nuevos
              </div>
            </div>
            <div className="text-center p-4 bg-muted rounded-xl">
              <div className="text-2xl font-bold text-muted-foreground">
                {salesData.customerStats.generic_customers}
              </div>
              <div className="text-sm text-muted-foreground font-medium">
                Consumidor Final
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Porcentaje de clientes nuevos</span>
              <span className="font-semibold text-foreground">
                {salesData.customerStats.new_customer_percentage.toFixed(1)}%
              </span>
            </div>
            <Progress value={salesData.customerStats.new_customer_percentage} className="h-3" />
          </div>

          <div className="flex items-center justify-between p-3 bg-background/60 rounded-xl border border-border/20">
            <span className="text-sm text-muted-foreground">Total ventas hoy:</span>
            <span className="font-bold text-primary">{salesData.customerStats.total_sales_today}</span>
          </div>

          <div className="flex gap-2 pt-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/customers')}
              className="flex-1 text-xs"
            >
              <Users className="h-3 w-3 mr-1" />
              Ver Clientes
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/sales')}
              className="flex-1 text-xs"
            >
              <TrendingUp className="h-3 w-3 mr-1" />
              Ver Ventas
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}